import { useEffect, useMemo, useState } from 'react';
import { formatDuration, formatHours } from '@chronos/shared';
import { useAuth } from '@/auth/AuthContext';
import { useCurrentEntry, useMyProjects, useTimerActions, useTimesheet } from '@/hooks/queries';
import { Card, Dot, EmptyState, ErrorNote, Modal, PageHeader, Spinner, StatTile, SubmitButton } from '@/components/ui';
import { addDays, formatTime, startOfDay, startOfWeek, toDateTimeInput } from '@/lib/format';
import { isDesktopApp } from '@/lib/desktop';
import { useElapsed } from '@/lib/elapsed';
import { DesktopPanel } from '@/components/DesktopPanel';

export function TrackerPage() {
  const { settings, breakProject } = useAuth();
  const projects = useMyProjects();
  const { data: current, isLoading } = useCurrentEntry();
  const { start, stop } = useTimerActions();

  const [projectId, setProjectId] = useState('');
  const [description, setDescription] = useState('');
  const [manualOpen, setManualOpen] = useState(false);

  // Default to the first assigned project once the list loads.
  useEffect(() => {
    if (!projectId && projects.data?.length) setProjectId(projects.data[0].id);
  }, [projects.data, projectId]);

  const today = useTimesheet(startOfDay().toISOString(), addDays(startOfDay(), 1).toISOString());
  const week = useTimesheet(startOfWeek().toISOString(), addDays(startOfDay(), 1).toISOString());

  const elapsed = useElapsed(current);
  const recent = useMemo(() => (today.data?.entries ?? []).slice().reverse().slice(0, 8), [today.data]);
  const onBreak = !!current && !!breakProject && current.projectId === breakProject.id;

  const breakButton = breakProject && (
    <button
      type="button"
      className={onBreak ? 'btn bg-amber-500 text-white hover:bg-amber-600' : 'btn border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'}
      disabled={start.isPending || stop.isPending}
      onClick={() => (onBreak ? stop.mutate(current!.id) : start.mutate({ projectId: breakProject.id, description: 'Break', source: 'WEB' }))}
      title={onBreak ? 'End break' : `Take a break (logs to ${breakProject.name})`}
    >
      ☕ {onBreak ? 'End break' : 'Break'}
    </button>
  );

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Timer"
        description="Clock in against a project, and stop when you're done."
        actions={
          settings?.allowManualTimeEntry !== false && (
            <button className="btn-secondary" onClick={() => setManualOpen(true)}>
              Add time manually
            </button>
          )
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <StatTile label="Today" value={formatHours(today.data?.totalSeconds ?? 0)} />
        <StatTile label="This week" value={formatHours(week.data?.totalSeconds ?? 0)} />
        <StatTile
          label="Status"
          value={current ? 'Clocked in' : 'Idle'}
          tone={current ? 'green' : 'default'}
          hint={current ? `since ${formatTime(current.startTime)}` : undefined}
        />
      </div>

      <Card className="mt-4 p-5">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : current ? (
          <div className="flex flex-col items-center gap-4 py-4">
            <p className="text-sm text-slate-500">Tracking time on</p>
            <div className="flex items-center gap-2">
              <Dot color={current.projectColor ?? '#3182f6'} />
              <span className="text-lg font-medium text-slate-900">{current.projectName}</span>
            </div>
            {current.description && <p className="text-sm text-slate-600">{current.description}</p>}
            <p className="font-mono text-5xl font-semibold tabular-nums tracking-tight text-slate-900">{formatDuration(elapsed)}</p>
            <div className="flex items-center gap-2">
              {breakButton}
              <button className="btn-danger px-8 py-2.5" onClick={() => stop.mutate(current.id)} disabled={stop.isPending}>
                {stop.isPending ? <Spinner className="h-4 w-4" /> : 'Stop timer'}
              </button>
            </div>
            {isDesktopApp() && (
              <p className="text-xs text-slate-500">
                Screenshots are captured every {settings?.screenshotIntervalMinutes ?? 10} minutes while this timer runs.
              </p>
            )}
          </div>
        ) : projects.data && projects.data.length === 0 ? (
          <EmptyState
            title="No projects assigned yet"
            description="An administrator needs to add you to a project before you can track time."
          />
        ) : (
          <form
            className="space-y-4 py-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (projectId) start.mutate({ projectId, description: description.trim() || null, source: isDesktopApp() ? 'DESKTOP' : 'WEB' });
            }}
          >
            <ErrorNote error={start.error} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="project">Project</label>
                <select id="project" className="input" value={projectId} onChange={(e) => setProjectId(e.target.value)} required>
                  {(projects.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.client ? ` · ${p.client}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label" htmlFor="desc">What are you working on? <span className="font-normal text-slate-400">(optional)</span></label>
                <input id="desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} placeholder="e.g. Sprint planning" />
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 pt-1">
              {breakButton}
              <SubmitButton pending={start.isPending} className="btn-primary px-10 py-2.5" disabled={!projectId}>
                Start timer
              </SubmitButton>
            </div>
          </form>
        )}
      </Card>

      <Card className="mt-4">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Today's entries</h2>
        </div>
        {recent.length === 0 ? (
          <EmptyState title="Nothing tracked today yet" />
        ) : (
          <ul className="divide-y divide-slate-100">
            {recent.map((e) => (
              <li key={e.id} className="flex items-center gap-3 px-4 py-3">
                <Dot color={e.projectColor ?? '#94a3b8'} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800">{e.projectName}</p>
                  <p className="truncate text-xs text-slate-500">{e.description || 'No description'}</p>
                </div>
                <span className="hidden text-xs text-slate-500 sm:block">
                  {formatTime(e.startTime)} – {e.endTime ? formatTime(e.endTime) : 'now'}
                </span>
                <span className="w-20 text-right font-mono text-sm tabular-nums text-slate-700">
                  {formatDuration(e.durationSeconds ?? 0)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <DesktopPanel />

      <ManualEntryModal open={manualOpen} onClose={() => setManualOpen(false)} />
    </div>
  );
}

export function ManualEntryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const projects = useMyProjects();
  const { createManual } = useTimerActions();
  const [projectId, setProjectId] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState(() => toDateTimeInput(new Date(Date.now() - 3600_000)));
  const [endTime, setEndTime] = useState(() => toDateTimeInput(new Date()));

  useEffect(() => {
    if (!projectId && projects.data?.length) setProjectId(projects.data[0].id);
  }, [projects.data, projectId]);

  return (
    <Modal
      open={open}
      title="Add a time entry"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <SubmitButton
            form="manual-entry-form"
            pending={createManual.isPending}
            className="btn-primary"
          >
            Save entry
          </SubmitButton>
        </>
      }
    >
      <form
        id="manual-entry-form"
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          createManual.mutate(
            {
              projectId,
              description: description.trim() || null,
              // datetime-local values are local time; the Date constructor keeps that.
              startTime: new Date(startTime).toISOString(),
              endTime: new Date(endTime).toISOString(),
            },
            { onSuccess: onClose },
          );
        }}
      >
        <ErrorNote error={createManual.error} />
        <div>
          <label className="label" htmlFor="mproject">Project</label>
          <select id="mproject" className="input" value={projectId} onChange={(e) => setProjectId(e.target.value)} required>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="mstart">Start</label>
            <input id="mstart" type="datetime-local" className="input" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
          </div>
          <div>
            <label className="label" htmlFor="mend">End</label>
            <input id="mend" type="datetime-local" className="input" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
          </div>
        </div>
        <div>
          <label className="label" htmlFor="mdesc">Description</label>
          <input id="mdesc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
        </div>
      </form>
    </Modal>
  );
}
